import { useEffect, useRef, useState } from 'react'

import type { LogVessel } from '../api'
import { playheadEpoch, type Playhead } from '../playhead'

/* Public Mapbox token, baked in at build time. Browser-safe by design — it
 * signs nothing and buys nothing but raster tiles — but it must be a
 * URL-restricted token, never a secret one. Absent, the map simply does not
 * render and the vessel table below it still says everything; a degradation
 * rather than a failure, which is the same bargain DeployMap makes. */
const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

export interface Senter { lat: number; lon: number }

/** Så nær må et fartøy være for å være med på å sette rammen.
 *
 *  500 m er ikke et pent tall: det er avstanden § 18 setter for
 *  ferdselsforbud til havs, og den avstanden lyd fra en arbeidsbåt fortsatt
 *  dominerer det en hydrofon på anlegget hører. */
const RAMME_M = 500

/** Fartøyene i én time, der de faktisk var.
 *
 *  En avstand er et tall; en posisjon er et bilde. «180 m» og «180 m, forbi
 *  nordenden og ned i null fart» er ulike mengder kunnskap, og den andre er
 *  det som får en driftsleder til å si «det er vår båt» framfor «en båt».
 *
 *  Satellitt, ikke gatekart, av samme grunn som DeployMap: motivet er vann. Et
 *  gatekart av en fjord er en tom blå flate, mens et anlegg leses som en ring
 *  av merder du kjenner igjen.
 *
 *  Lastes dynamisk, som hls.js og DeployMap. MapLibre er omtrent dobbelt så
 *  stor som resten av denne appen til sammen, og den trengs når noen folder ut
 *  én time — ikke når noen åpner loggen for å se på ringen.
 *
 *  Kartet rammer seg selv rundt anlegget og båtene, med et tak på zoomen: to
 *  båter som lå tett ville ellers gitt et kart der ingenting annet enn dem er
 *  synlig, og da er rammen ikke lenger en opplysning om hvor de var.
 */
export function HourMap({ senter, fartoy, ph, hoyde = 210 }: {
  senter: Senter
  fartoy: LogVessel[]
  /** Spilleren, når en finnes. Mens et klipp spiller går båtene der de faktisk
   *  gikk, i sanntid: ett sekund lyd er ett sekund bevegelse. Det er ikke
   *  pynt -- det er den ene måten «lå stille» og «passerte» kan skilles mens
   *  du hører på, og for en båt i åtte knop er det 250 meter i minuttet, som
   *  er godt synlig i en ramme på 500. */
  ph?: Playhead
  hoyde?: number
}) {
  const el = useRef<HTMLDivElement>(null)
  const map = useRef<any>(null)
  const [feilet, setFeilet] = useState(false)
  const [klar, setKlar] = useState(0)
  /** Merkene som kan flyttes, med sporet sitt. Skrevet av effekten under og
   *  lest av animasjonsløkka; ingen tilstand, fordi ingenting skal rendres på
   *  nytt for at en trekant skal flytte seg. */
  const levende = useRef<{ mk: any; spor: [number, number, number][];
                           hvile: [number, number]; el: HTMLElement }[]>([])

  useEffect(() => {
    if (!TOKEN) { setFeilet(true); return }
    let dod = false
    let m: any

    void (async () => {
      try {
        const maplibregl = (await import('maplibre-gl')).default
        await import('maplibre-gl/dist/maplibre-gl.css')
        if (dod || !el.current) return

        m = new maplibregl.Map({
          container: el.current,
          attributionControl: false,
          // Ingen interaksjon: dette er et bilde av en time, ikke et kart å
          // navigere i. En rulling som stjeler siden mens du blar i lista er
          // den vanligste måten et innfelt kart blir irriterende på.
          interactive: false,
          style: {
            version: 8,
            sources: {
              satellite: {
                type: 'raster',
                tiles: [`https://api.tiles.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg?access_token=${TOKEN}`],
                tileSize: 256,
                attribution: '© Mapbox · © OpenStreetMap',
              },
            },
            layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
          },
          center: [senter.lon, senter.lat],
          zoom: 13,
        })
        map.current = m
        m.addControl(new maplibregl.AttributionControl({ compact: true }),
                     'bottom-right')
        /* Først når stilen er lastet. addSource på et kart som ennå ikke har
           lest stilen sin kaster «Style is not done loading», og fordi det
           skjer inne i en async-funksjon uten catch, dør resten av løkka
           stille: ingen båter, ingen ramme, bare satellitt og et rødt punkt.
           Det er en kappløpsfeil, så den viser seg på en treg linje og ikke på
           en rask. */
        m.on('load', () => { if (!dod) setKlar((n) => n + 1) })
        m.on('error', (e: any) => {
          /* Fliser som ikke kommer er den ene feilen som ellers er taus: en
             blokkert CSP eller et avvist token gir et svart rektangel som ser
             ut som tomt hav. */
          if (e?.error?.status === 401 || e?.error?.status === 403) setFeilet(true)
        })
      } catch {
        if (!dod) setFeilet(true)
      }
    })()

    return () => { dod = true; try { m?.remove() } catch { /* alt borte */ } }
  }, [])

  useEffect(() => {
    const m = map.current
    if (!m || klar === 0) return

    /* Anlegget: samme røde punkt som ellers betyr «her er tingen selv». */
    const anlegg = document.createElement('div')
    anlegg.className = 'kart-anlegg'
    const markorer: any[] = []

    void (async () => {
      const maplibregl = (await import('maplibre-gl')).default
      markorer.push(new maplibregl.Marker({ element: anlegg })
        .setLngLat([senter.lon, senter.lat]).addTo(m))

      /* Rammen settes av de NÆRE båtene, ikke av alle.
       *
       * Kartet svarer på «hvilken båt lagde lyden», og det er de nærmeste.
       * Et fartøy 1,7 km unna er kontekst -- det står i tabellen under -- men
       * lar man det sette rammen, blir de tre båtene som faktisk lå ved merden
       * en klynge prikker i midten, og bildet slutter å si noe. Finnes ingen
       * nær båt, rammes den nærmeste som finnes, så kartet aldri er tomt. */
      const naere = fartoy.filter((v) => (v.closest_m ?? 1e9) <= RAMME_M)
      const rammer = naere.length ? naere
        : fartoy.slice(0, 1)
      const punkter: [number, number][] = [[senter.lon, senter.lat]]

      for (const v of fartoy) {
        if (v.closest_lat == null || v.closest_lon == null) continue
        if (rammer.includes(v)) punkter.push([v.closest_lon, v.closest_lat])

        /* Sporet gjennom timen, som en tynn linje. Det er dette som skiller
           «lå her» fra «kom forbi»: to båter på samme avstand er ikke samme
           hendelse hvis den ene stod stille og den andre passerte. */
        const spor = (v.track ?? []).filter((p) => p[1] != null && p[2] != null)
        if (spor.length > 1) {
          const id = `spor-${v.mmsi}`
          if (!m.getSource(id)) {
            m.addSource(id, {
              type: 'geojson',
              data: {
                type: 'Feature', properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: spor.map((p) => [p[2], p[1]]),
                },
              },
            })
            m.addLayer({
              id, type: 'line', source: id,
              paint: {
                'line-color': v.aqua_fleet ? '#b4d9c7' : '#93ab9f',
                'line-width': 1.5,
                'line-opacity': 0.85,
              },
            })
          }
        }

        /* Navnet står PÅ kartet, ikke i en tabell under det. Tabellen sa
           navn og avstand ved siden av et bilde som allerede viste begge
           deler; å flytte teksten inntil merket fjerner et helt element fra
           siden uten å fjerne en eneste opplysning.

           Bare de nære merkes. Et fartøy 1,7 km unna er kontekst, og en
           etikett på det ville ligge oppå de tre som saken handler om. */
        const merket = rammer.includes(v)

        const wrap = document.createElement('div')
        wrap.className = 'kart-merke'

        const b = document.createElement('div')
        b.className = `kart-baat${v.aqua_fleet ? ' akva' : ''}`
        /* Pekt dit den faktisk gikk. En båt som peker feil vei er verre enn en
           uten retning: den forteller noe, og det er galt. */
        const kurs = spor.length ? spor[spor.length - 1]![4] : null
        if (kurs != null) b.style.transform = `rotate(${kurs}deg)`
        wrap.append(b)

        if (merket) {
          const navn = document.createElement('span')
          navn.className = 'kart-navn'
          navn.textContent = `${v.name ?? v.mmsi} ${
            (v.closest_m ?? 0) < 1000 ? `${Math.round(v.closest_m ?? 0)} m`
              : `${((v.closest_m ?? 0) / 1000).toFixed(1)} km`}`
          wrap.append(navn)
        }
        wrap.title = `${v.name ?? v.mmsi} · ${Math.round(v.closest_m ?? 0)} m`

        const mk = new maplibregl.Marker({ element: wrap })
          .setLngLat([v.closest_lon, v.closest_lat]).addTo(m)
        markorer.push(mk)
        levende.current.push({
          mk, el: wrap,
          spor: spor.map((p) => [p[0], p[1], p[2]] as [number, number, number]),
          hvile: [v.closest_lon, v.closest_lat],
        })
      }

      if (punkter.length > 1) {
        const lons = punkter.map((p) => p[0])
        const lats = punkter.map((p) => p[1])
        m.fitBounds(
          [[Math.min(...lons), Math.min(...lats)],
           [Math.max(...lons), Math.max(...lats)]],
          /* Taket på zoomen: to båter som lå tett ville ellers fylt kartet med
             bare dem, og da sier rammen ingenting om hvor de var. */
          { padding: 42, maxZoom: 16.5, duration: 0 })
      } else {
        m.jumpTo({ center: [senter.lon, senter.lat], zoom: 15 })
      }
    })()

    return () => {
      levende.current = []
      for (const mk of markorer) { try { mk.remove() } catch { /* */ } }
    }
  }, [klar, fartoy, senter.lat, senter.lon])

  /* Båtene går, mens lyden går.
   *
   * Interpolert mellom AIS-meldingene og aldri ekstrapolert: utenfor sporets
   * ender står båten der den nærmeste passeringen var. En båt tegnet der AIS
   * ikke sa den var er en påstand vi ikke har dekning for, og den ville vært
   * usynlig som feil nettopp fordi den beveger seg troverdig. */
  useEffect(() => {
    if (!ph) return
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) return
    let raf = 0
    const steg = () => {
      const t = playheadEpoch(ph)
      for (const l of levende.current) {
        let pos = l.hvile
        if (t !== null && l.spor.length > 1
            && t >= l.spor[0]![0] && t <= l.spor[l.spor.length - 1]![0]) {
          for (let i = 1; i < l.spor.length; i++) {
            const b = l.spor[i]!
            if (t <= b[0]) {
              const a = l.spor[i - 1]!
              const spenn = b[0] - a[0]
              const k = spenn > 0 ? (t - a[0]) / spenn : 0
              pos = [a[2] + (b[2] - a[2]) * k, a[1] + (b[1] - a[1]) * k]
              break
            }
          }
        }
        try { l.mk.setLngLat(pos) } catch { /* kartet er borte */ }
        l.el.classList.toggle('gaar', t !== null)
      }
      raf = requestAnimationFrame(steg)
    }
    raf = requestAnimationFrame(steg)
    return () => cancelAnimationFrame(raf)
  }, [ph, klar, fartoy])

  if (feilet) return null

  return <div className="kart" style={{ height: hoyde }} ref={el} />
}
